import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TriangleAlert } from 'lucide-react';

import { deleteAgentConversationSchema } from '@/app/agentConversation/_validators';

import { Alert, AlertDescription, AlertTitle } from '@/app/ui/alert';
import { Button } from '@/app/ui/button';
import { Checkbox } from '@/app/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/app/ui/dialog';
import { Error } from '@/app/ui/error';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/app/ui/form';
import { Spinner } from '@/app/ui/spinner';
import { useAgentConversationObject, useDeleteAgentConversation } from '@/data/agentConversation';

const DeleteAgentConversationDialog = ({ uid, onClose, onSuccess }) => {
  const conversation = useAgentConversationObject(uid);
  const deleteAgentConversation = useDeleteAgentConversation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const form = useForm({
    resolver: zodResolver(deleteAgentConversationSchema),
    defaultValues: { is_confirmed: false }
  });

  const onSubmit = async () => {
    setApiError('');
    setIsSubmitting(true);
    try {
      await deleteAgentConversation(uid);
      await onSuccess(uid);
      onClose();
    } catch (error) {
      setIsSubmitting(false);
      setApiError(error.message);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Conversation</DialogTitle>
          <DialogDescription>{conversation.title}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <Alert variant="destructive">
              <TriangleAlert className="h-4 w-4" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>Deleting this conversation is permanent and cannot be undone.</AlertDescription>
            </Alert>
            <FormField
              control={form.control}
              name="is_confirmed"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="confirm-checkbox" />
                    </FormControl>
                    <FormLabel>I want to delete this conversation</FormLabel>
                  </div>
                  <FormMessage data-testid="confirm-checkbox-error" />
                </FormItem>
              )}
            />
            {apiError && <Error message={apiError} dataTestId="api-error" />}
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting} variant="destructive" data-testid="modal-submit-button">
                {isSubmitting && <Spinner />}
                Delete Conversation
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAgentConversationDialog;
